import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Messaging,
  MessagePayload,
  Unsubscribe,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class FcmTokenService implements OnDestroy {
  private static readonly SW_URL = '/firebase-messaging-sw.js';
  private static readonly TOKEN_TIMEOUT_MS = 10_000;
  private static readonly SW_ACTIVATION_TIMEOUT_MS = 8_000;

  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private swRegistration: ServiceWorkerRegistration | null = null;

  private cachedToken: string | null = null;
  private inflightToken: Promise<string | null> | null = null;

  private onMessageUnsubscribe: Unsubscribe | null = null;
  private readonly foregroundMessageSubject = new Subject<MessagePayload>();

  readonly foregroundMessages$: Observable<MessagePayload> =
    this.foregroundMessageSubject.asObservable();

  constructor(private readonly zone: NgZone) {}

  ngOnDestroy(): void {
    this.onMessageUnsubscribe?.();
    this.foregroundMessageSubject.complete();
  }

 
  async getToken(forceRefresh = false): Promise<string | null> {
    if (!forceRefresh && this.cachedToken) {
      return this.cachedToken;
    }
    if (this.inflightToken) {
      return this.inflightToken;
    }

    this.inflightToken = this.requestToken().finally(() => {
      this.inflightToken = null;
    });
    return this.inflightToken;
  }

  hasRealToken(): boolean {
    return !!this.cachedToken;
  }

  async invalidateToken(): Promise<void> {
    this.cachedToken = null;
    if (!this.messaging) return;
    try {
      await deleteToken(this.messaging);
    } catch (e) {
      this.warn('deleteToken failed (ignored)', e);
    }
  }

  private async requestToken(): Promise<string | null> {
    try {
      if (!this.isConfigComplete()) {
        this.error(
          'environment.firebase is not filled in (apiKey / projectId / ' +
            'messagingSenderId / appId are required) — returning null',
        );
        return null;
      }

      if (!(await this.isFcmSupported())) {
        this.warn(
          'FCM not supported here (needs HTTPS or localhost, service worker, PushManager) — returning null',
        );
        return null;
      }

      const permission = await this.ensureNotificationPermission();
      if (permission !== 'granted') {
        this.warn(`Notification permission is "${permission}" — returning null`);
        return null;
      }

      const messaging = await this.initMessaging();
      if (!messaging) {
        return null;
      }

      const token = await this.withTimeout(
        getToken(messaging, {
          vapidKey: environment.firebaseVapidKey || undefined,
          serviceWorkerRegistration: this.swRegistration ?? undefined,
        }),
        FcmTokenService.TOKEN_TIMEOUT_MS,
        'getToken()',
      );

      if (!token) {
        this.error('getToken() returned empty or timed out — returning null');
        return null;
      }

      this.cachedToken = token;
      return token;
    } catch (e) {
      this.error('Token retrieval failed — returning null', e);
      return null;
    }
  }

  private isConfigComplete(): boolean {
    const c = environment.firebase;
    return !!(c?.apiKey && c?.projectId && c?.messagingSenderId && c?.appId);
  }

  private async isFcmSupported(): Promise<boolean> {
    try {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
      if (!window.isSecureContext) return false;
      return await isSupported();
    } catch {
      return false;
    }
  }

  private async ensureNotificationPermission(): Promise<NotificationPermission> {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission !== 'default') return Notification.permission;
    try {
      return await Notification.requestPermission();
    } catch {
      return 'denied';
    }
  }

  private async initMessaging(): Promise<Messaging | null> {
    if (this.messaging) return this.messaging;

    try {
      this.app = getApps().length ? getApp() : initializeApp(environment.firebase);
      this.swRegistration = await this.registerServiceWorker();
      this.messaging = getMessaging(this.app);
      this.listenForForegroundMessages(this.messaging);
      return this.messaging;
    } catch (e) {
      this.error('Firebase / service-worker init failed', e);
      this.messaging = null;
      this.swRegistration = null;
      return null;
    }
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const config = environment.firebase as Record<string, string>;
    const params = new URLSearchParams(
      Object.entries(config).filter(([, v]) => !!v) as [string, string][],
    );
    const swUrl = `${FcmTokenService.SW_URL}?${params.toString()}`;

    const existing = await navigator.serviceWorker.getRegistration(FcmTokenService.SW_URL);
    const registration = existing ?? (await navigator.serviceWorker.register(swUrl));

    await this.waitForActivation(registration);
    return registration;
  }

  private waitForActivation(registration: ServiceWorkerRegistration): Promise<void> {
    if (registration.active) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const worker = registration.installing ?? registration.waiting;
      if (!worker) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        this.warn('Service worker did not activate in time — continuing anyway');
        resolve();
      }, FcmTokenService.SW_ACTIVATION_TIMEOUT_MS);

      const onStateChange = () => {
        if (worker.state === 'activated' || worker.state === 'redundant') {
          clearTimeout(timeout);
          worker.removeEventListener('statechange', onStateChange);
          resolve();
        }
      };
      worker.addEventListener('statechange', onStateChange);
    });
  }

  private listenForForegroundMessages(messaging: Messaging): void {
    if (this.onMessageUnsubscribe) return;
    this.onMessageUnsubscribe = onMessage(messaging, (payload) => {
      this.zone.run(() => this.foregroundMessageSubject.next(payload));
    });
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => {
        this.warn(`${label} timed out after ${ms}ms`);
        resolve(null);
      }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T | null>;
  }

  private warn(message: string, e?: unknown): void {
    console.warn(`[FcmTokenService] ${message}`, e ?? '');
  }

  private error(message: string, e?: unknown): void {
    console.error(`[FcmTokenService] ${message}`, e ?? '');
  }
}
